import { ChangeDetectionStrategy, Component, inject, computed, signal, Renderer2 } from '@angular/core';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { CommonModule, Location, NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HeaderState } from '../../@core/services/header-state.service';
import { ToastContainerComponent } from 'src/app/app-component/toast-container.component';
import { GlobalLoaderComponent } from 'src/app/app-component/global-loader-component';
import { SidebarComponent } from 'src/app/shared/components/sidebar/sidebar-component';
import { StoragePrefix } from '@core/constant';
import {
  AfterViewInit,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import { StorageService } from 'src/app/utils';
import { LanguageModalComponent } from "src/app/shared/components/language-modal.component/language-modal.component";
import { Popup } from 'src/app/shared/components/popup/popup';
import { IdleTimeoutService } from '@core/services/idle-timeout.service';
import { PortalHostDirective } from 'src/app/shared/directives/portal-host-directive';
import { Subscription, filter } from 'rxjs';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TranslateModule,
    CommonModule,
    GlobalLoaderComponent,
    ToastContainerComponent,
    SidebarComponent,
    LanguageModalComponent,
    PortalHostDirective,
    RouterModule,
    Popup,
    NgClass
  ],
  templateUrl: './main-layout-component.html',
  styleUrls: ['./main-layout-component.scss'],
})
export class MainLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  _storage = inject(StorageService);
  _idleTime = inject(IdleTimeoutService);
  isDashboard = signal(false);
  constructor(
    private location: Location,
    public translate: TranslateService,
    private renderer: Renderer2
  ) { }
  // ---- UI state
  hasUnreadNotifications = false;
  menuOpen = signal<boolean>(true);

  // ---- Header animation vars (used in template/styles)
  headerH = 220; // starting height
  private readonly minH = 180;
  private readonly maxH = 360;
  private readonly k = 0.25;
  // currentLang = this._storage.get('LANG') || 'en';

  screenWidth = signal(window.innerWidth);

  // ---- Injected header state signals
  public header = inject(HeaderState);
  title = computed(() => this.header.title());
  wave = computed(() => this.header.wave());
  router = inject(Router);

  // ---- User info
  personalInfo: any = this._storage.get(StoragePrefix.PERSONAL_INFO) ?? {};
  // fullName: string = [
  //   this.personalInfo?.firstName ?? '',
  //   this.personalInfo?.lastName ?? '',
  //   '',
  // ]
  fullName: string = [
    this.personalInfo?.firstName ?? '',
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  // ---- Sidebar measurements
  @ViewChild('sidebar') sidebarRef?: ElementRef<HTMLElement>;
  sidebarWidth = 281;
  private ro?: ResizeObserver;
  private routerEventsSub?: Subscription;

  // ========== Handlers ==========

  /** Matches template breakpoint: mobile curved header <= 600 */
  private readonly mobileMaxWidth = 600;
  private readonly sidebarExpandedWidth = 281;
  private readonly sidebarCollapsedWidth = 72;
  isMdUp = signal<boolean>(window.innerWidth > 600);
  isLangPopUpVisible = signal(false);
  isSessionExpired = signal(false);

  toggleMenu() {
    this.menuOpen.update((open) => !open);
  }

  /**
   * Desktop shell padding reserves space for the fixed sidebar
   * (expanded 281 / collapsed rail 72) without margin+width overflow.
   */
  shellClasses = computed(() => {
    const isDesktop = this.screenWidth() > this.mobileMaxWidth;
    if (!isDesktop) {
      return {};
    }

    const isLTR = this.currentLang === 'en';
    const isOpen = this.menuOpen();
    return {
      'pl-[281px]': isLTR && isOpen,
      'pr-[281px]': !isLTR && isOpen,
      'pl-[72px]': isLTR && !isOpen,
      'pr-[72px]': !isLTR && !isOpen,
    };
  });

  /** Main content padding only (sidebar offset handled by shellClasses). */
  dynamicClasses = computed(() => {
    const isMobile = this.screenWidth() <= this.mobileMaxWidth;
    const isDesktop = !isMobile;

    return {
      ...(isMobile && {
        'bg-white space-y-6 px-5 py-8': true,
      }),
      ...(isDesktop && {
        'bg-white min-h-[calc(100vh-56px)] w-full max-w-full px-8 py-4': true,
      }),
    };
  });

  openMenu() {
    this.menuOpen.set(true);
  }

  closeMenu() {
    this.menuOpen.set(false);
  }

  openNotifications() {
    // e.g. navigate to notifications in the future
  }

  goBack() {
    this.location.back();
  }

  switchLang(arg: any) {
    const lang = typeof arg === 'string' ? arg : arg?.target?.value;
    if (!lang) return;
    this.translate.use(lang);
    document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    this._storage.set(StoragePrefix.LANGUAGE, lang);
  }

  // get currentLang(): string {
  //   return (
  //     this.translate.currentLang || this.translate.getDefaultLang() || 'en'
  //   );
  // }

  // ========== Lifecycle ==========
  ngOnInit() {
    this.isDashboard.set(this.router.url.includes('/dashboard'));
    this.routerEventsSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.isDashboard.set(event.urlAfterRedirects.includes('/dashboard'));
      });
    this.header.isNameShow.set(true);
    this._idleTime.startWatching();
    // this.renderer.listen('window', 'resize', this.onResize.bind(this));
    const saved = this._storage.get(StoragePrefix.LANGUAGE) || 'en';
    this._storage.lang.set(saved);
    this.translate.setDefaultLang('en');
    this.translate.use(saved);
    document.dir = saved === 'ar' ? 'rtl' : 'ltr';
    try {
      document.documentElement.lang = saved;
    } catch { }
    const desktop = window.innerWidth > this.mobileMaxWidth;
    this.isMdUp.set(desktop);
    // Desktop starts open; mobile starts closed
    this.menuOpen.set(desktop);
  }

  ngAfterViewInit(): void {
    this.initResizeObserver();
    // Run once in case sidebar is already visible
    setTimeout(() => this.updateSidebarWidth(), 0);
  }

  ngOnDestroy(): void {
    // if (this.portalHost) {
    //   this._portal.clearHost(this.portalHost);
    // }
    this.ro?.disconnect();
    this.routerEventsSub?.unsubscribe();
  }

  // ========== Resize logic ==========
  @HostListener('window:resize')
  onWindowResize() {
    this.updateSidebarWidth();
    this.onResize();
  }

  onResize() {
    const width = window.innerWidth;
    this.screenWidth.set(width);
    const desktop = width > this.mobileMaxWidth;
    // Only reset open state when crossing mobile/desktop boundary
    if (desktop !== this.isMdUp()) {
      this.isMdUp.set(desktop);
      this.menuOpen.set(desktop);
    }
  }

  private initResizeObserver() {
    if (typeof window === 'undefined') return;
    // Guard: ResizeObserver may not exist in very old browsers
    if (!('ResizeObserver' in window)) return;

    this.ro = new ResizeObserver(() => this.updateSidebarWidth());
    const el = this.sidebarRef?.nativeElement;
    if (el) {
      this.ro.observe(el);
    }
  }

  private updateSidebarWidth() {
    const el = this.sidebarRef?.nativeElement;
    this.sidebarWidth =
      el && this.menuOpen() ? Math.round(el.getBoundingClientRect().width) : 0;
  }

  /** Prefer StorageService.lang() signal in templates; getter kept for TS call sites. */
  get currentLang(): string {
    return this._storage.lang() || this._storage.get(StoragePrefix.LANGUAGE) || 'en';
  }
  openLanguagePopUp() {
    this.isLangPopUpVisible.set(true)
  }
  check() {

  }
  handleSessionExpired(evt: any) {
    this._idleTime.confirmLogout();
  }
}
